import React, { useEffect, useMemo, useState } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import ActionDialogShell from '../newsQueue/ArticleView/dialogs/ActionDialogShell';

const DEFAULT_DAYS = 7;
const MIN_DAYS = 1;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isAlertSuppressed(alert, now = Date.now()) {
    if (!alert?.suppress_until) {
        return false;
    }
    const until = new Date(alert.suppress_until).getTime();
    return Number.isFinite(until) && until > now;
}

function remainingDays(alert, now = Date.now()) {
    if (!alert?.suppress_until) {
        return DEFAULT_DAYS;
    }
    const until = new Date(alert.suppress_until).getTime();
    if (!Number.isFinite(until) || until <= now) {
        return DEFAULT_DAYS;
    }
    return Math.max(MIN_DAYS, Math.ceil((until - now) / MS_PER_DAY));
}

export default function SuppressAlertDialog({
    open,
    alert,
    onClose,
    onConfirm,
    onClear,
    isPending = false,
    errorMessage = null,
}) {
    const intl = useIntl();
    const [days, setDays] = useState(String(DEFAULT_DAYS));
    const editing = useMemo(() => isAlertSuppressed(alert), [alert]);

    useEffect(() => {
        if (open) {
            setDays(String(remainingDays(alert)));
        }
    }, [open, alert?.id, alert?.suppress_until]);

    const parsedDays = Number.parseInt(days, 10);
    const isValidDays = Number.isFinite(parsedDays) && parsedDays >= MIN_DAYS;

    const handleConfirm = () => {
        if (!isValidDays || !alert?.id) {
            return;
        }
        onConfirm({ alertId: alert.id, days: parsedDays });
    };

    const handleClear = () => {
        if (!alert?.id) {
            return;
        }
        onClear?.({ alertId: alert.id });
    };

    const titleId = editing
        ? 'monitoring.alerts.suppress.editTitle'
        : 'monitoring.alerts.suppress.title';
    const descriptionId = editing
        ? 'monitoring.alerts.suppress.editDescription'
        : 'monitoring.alerts.suppress.description';
    const confirmId = editing
        ? 'monitoring.alerts.suppress.updateConfirm'
        : 'monitoring.alerts.suppress.confirm';

    return (
        <ActionDialogShell
            open={open}
            onClose={onClose}
            title={intl.formatMessage({ id: titleId })}
            actions={
                <>
                    {editing && (
                        <Button
                            color="error"
                            disabled={isPending}
                            onClick={handleClear}
                        >
                            {intl.formatMessage({ id: 'monitoring.alerts.suppress.clear' })}
                        </Button>
                    )}
                    <Button disabled={isPending} onClick={onClose}>
                        {intl.formatMessage({ id: 'newsQueue.common.cancel' })}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isValidDays || isPending || !alert}
                        onClick={handleConfirm}
                    >
                        {intl.formatMessage({ id: confirmId })}
                    </Button>
                </>
            }
        >
            <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage(
                        { id: descriptionId },
                        { sourceId: alert?.source_id ?? '–' }
                    )}
                </Typography>
                {editing && alert?.suppressed_by && (
                    <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage(
                            { id: 'monitoring.alerts.suppress.byUser' },
                            { userName: alert.suppressed_by }
                        )}
                    </Typography>
                )}
                <TextField
                    autoFocus
                    fullWidth
                    type="number"
                    label={intl.formatMessage({ id: 'monitoring.alerts.suppress.daysLabel' })}
                    helperText={intl.formatMessage({ id: 'monitoring.alerts.suppress.daysHelp' })}
                    value={days}
                    onChange={(event) => setDays(event.target.value)}
                    inputProps={{ min: MIN_DAYS, step: 1 }}
                    disabled={isPending}
                />
                {errorMessage && (
                    <Typography variant="body2" color="error">
                        {errorMessage}
                    </Typography>
                )}
            </Stack>
        </ActionDialogShell>
    );
}
